'use client';

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarRange, Trash2, X } from 'lucide-react';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';
import type { Temporada, TemporadaForm } from '@/components/hooks/useTemporadas';

type Props = {
  open: boolean;
  onClose: () => void;
  temporada: Temporada | null;
  saving: boolean;
  onCrear: (form: TemporadaForm) => Promise<{ ok: boolean }>;
  onEditar: (idTemporada: string, form: TemporadaForm) => Promise<{ ok: boolean }>;
  onBorrar: (idTemporada: string) => Promise<{ ok: boolean }>;
};

const VACIO: TemporadaForm = {
  nombre: '',
  fecha_desde: '',
  fecha_hasta: '',
};

export const TemporadaDialog = ({
  open,
  onClose,
  temporada,
  saving,
  onCrear,
  onEditar,
  onBorrar,
}: Props) => {
  const [form, setForm] = useState<TemporadaForm>(VACIO);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmarBorrado(false);
    setForm(
      temporada
        ? {
            nombre: temporada.nombre,
            fecha_desde: temporada.fecha_desde,
            fecha_hasta: temporada.fecha_hasta,
          }
        : VACIO
    );
  }, [open, temporada]);

  const handleGuardar = async () => {
    const { ok } = temporada
      ? await onEditar(temporada.id_horario_temporada, form)
      : await onCrear(form);
    if (ok) onClose();
  };

  const handleBorrar = async () => {
    if (!temporada) return;
    if (!confirmarBorrado) {
      setConfirmarBorrado(true);
      return;
    }
    const { ok } = await onBorrar(temporada.id_horario_temporada);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          z-[70] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              <CalendarRange className="w-5 h-5 shrink-0" />
              {temporada ? 'Editar temporada' : 'Nueva temporada'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
              Aquí defines el periodo. Las horas se ponen luego, en la lista de
              días, con la temporada elegida en el desplegable.
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              Nombre
            </label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Jornada de verano"
              className="h-10 text-sm rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Desde
              </label>
              <Input
                type="date"
                value={form.fecha_desde}
                onChange={(e) =>
                  setForm({ ...form, fecha_desde: e.target.value })
                }
                className="h-10 text-sm rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10"
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Hasta
              </label>
              <Input
                type="date"
                value={form.fecha_hasta}
                onChange={(e) =>
                  setForm({ ...form, fecha_hasta: e.target.value })
                }
                className="h-10 text-sm rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10"
              />
            </div>
          </div>

          {temporada && (
            <div className="pt-1">
              <Button
                variant="ghost"
                onClick={handleBorrar}
                disabled={saving}
                className="h-9 px-3 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                {confirmarBorrado
                  ? 'Pulsa otra vez para eliminarla y sus horarios'
                  : 'Eliminar temporada'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={saving}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {saving ? 'Guardando…' : temporada ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};